const DEFAULT_BASE_URL =
  "https://stickerchecker-new-722491567910.europe-west3.run.app";

export function getApiBaseUrl() {
  const url = import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL;
  if (!url) return DEFAULT_BASE_URL;
  if (url.startsWith("http://") && !url.includes("localhost")) {
    return url.replace("http://", "https://");
  }
  return url;
}

export function resolveMediaUrl(path, apiBaseUrl = getApiBaseUrl()) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = apiBaseUrl.replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

async function refreshAccessToken(apiBaseUrl) {
  const refreshToken = localStorage.getItem("refresh");
  if (!refreshToken) {
    throw new Error("No refresh token found. Please login again.");
  }

  const response = await fetch(`${apiBaseUrl}/api/v1/token/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  if (!response.ok) {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    throw new Error("Failed to refresh token. Please login again.");
  }

  const data = await response.json();
  localStorage.setItem("access", data.access);
  return data.access;
}

export async function authFetch(path, options = {}, apiBaseUrl = getApiBaseUrl()) {
  const url = path.startsWith("http")
    ? path
    : `${apiBaseUrl.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;

  let token = localStorage.getItem("access");
  if (!token) token = await refreshAccessToken(apiBaseUrl);

  let response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });

  if (response.status === 401) {
    token = await refreshAccessToken(apiBaseUrl);
    response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
  }

  return response;
}

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

/** Build card-list filters for eBay select-all sync / batch-preview (matches GET /cards/). */
export function buildEbaySyncFilters({ search, columnFilters } = {}) {
  const filters = {};

  if (search?.trim()) {
    filters.search = search.trim();
  }
  if (columnFilters?.status) {
    filters.status = columnFilters.status;
  }
  if (columnFilters?.ocr_status) {
    filters.ocr_status = columnFilters.ocr_status;
  }
  if (columnFilters?.ebay_sync_status) {
    filters.ebay_sync_status = columnFilters.ebay_sync_status;
  }

  return filters;
}

export function hasEbaySyncFilters(filters) {
  return Object.keys(filters || {}).length > 0;
}

export function buildSelectAllSyncPayload(batchId, filterInput) {
  const filters = buildEbaySyncFilters(filterInput);
  const payload = {
    select_all: true,
    batch_id: batchId,
  };

  if (hasEbaySyncFilters(filters)) {
    payload.filters = filters;
  }

  return payload;
}

function appendEbaySyncFiltersToParams(params, filterInput) {
  const filters = buildEbaySyncFilters(filterInput);
  Object.entries(filters).forEach(([key, value]) => {
    if (value != null && value !== "") {
      params.set(key, String(value));
    }
  });
}

export async function startEbaySync(payload, apiBaseUrl = getApiBaseUrl()) {
  const response = await authFetch(
    "/api/v1/ebay/sync/",
    { method: "POST", body: JSON.stringify(payload) },
    apiBaseUrl
  );
  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw new Error(
      data?.error || data?.detail || `Sync failed: ${response.status}`
    );
  }

  return data;
}

export async function getEbaySyncRun(syncRunId, apiBaseUrl = getApiBaseUrl()) {
  const response = await authFetch(
    `/api/v1/ebay/sync-runs/${syncRunId}/`,
    { method: "GET" },
    apiBaseUrl
  );
  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw new Error(
      data?.error || data?.detail || `Failed to load sync run: ${response.status}`
    );
  }

  return data;
}

export async function getEbaySyncRuns(page = 1, pageSize = 20, apiBaseUrl = getApiBaseUrl()) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  const response = await authFetch(
    `/api/v1/ebay/sync-runs/?${params.toString()}`,
    { method: "GET" },
    apiBaseUrl
  );
  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw new Error(
      data?.error || data?.detail || `Failed to load sync runs: ${response.status}`
    );
  }

  return data;
}

export async function getEbayBatchSyncStatus(batchId, apiBaseUrl = getApiBaseUrl()) {
  const params = new URLSearchParams({ batch_id: String(batchId) });
  const response = await authFetch(
    `/api/v1/ebay/sync/batch-status/?${params.toString()}`,
    { method: "GET" },
    apiBaseUrl
  );
  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw new Error(
      data?.error ||
        data?.detail ||
        `Failed to load batch sync status: ${response.status}`
    );
  }

  return data;
}

export async function getEbayBatchSyncPreview(
  batchId,
  filterInput = {},
  apiBaseUrl = getApiBaseUrl()
) {
  const params = new URLSearchParams({ batch_id: String(batchId) });
  appendEbaySyncFiltersToParams(params, filterInput);
  const response = await authFetch(
    `/api/v1/ebay/sync/batch-preview/?${params.toString()}`,
    { method: "GET" },
    apiBaseUrl
  );
  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw new Error(
      data?.error ||
        data?.detail ||
        `Failed to load batch preview: ${response.status}`
    );
  }

  return data;
}

export function formatSyncRunStatus(status) {
  const labels = {
    running: "Running",
    completed: "Completed",
    completed_with_errors: "Completed with errors",
    failed: "Failed",
  };
  return labels[status] || status || "Unknown";
}

export function getSyncProgressPercent(counts) {
  if (!counts || !counts.total) return 0;
  const done = counts.done ?? counts.listed + (counts.failed || 0);
  return Math.min(100, Math.round((done / counts.total) * 100));
}

export function isCardLockedForEdit(card) {
  return card?.status === "listed" || card?.status === "listing";
}

/**
 * Push a live price/quantity change to eBay for cards that are already listed,
 * via the backend's bulkUpdatePriceQuantity-backed endpoint. Runs asynchronously
 * on the backend; progress is reported over the ebay.price_quantity_update.progress
 * socket event (see subscribeEbayPriceQuantityUpdateProgress).
 */
const PQ_LOG = "[eBay:PriceQty]";

export async function triggerEbayPriceQuantityUpdate(
  cardIds,
  apiBaseUrl = getApiBaseUrl()
) {
  console.log(PQ_LOG, "1. Calling PUT /api/v1/ebay/update-bulk-offer/", {
    card_ids: cardIds,
  });

  const response = await authFetch(
    "/api/v1/ebay/update-bulk-offer/",
    { method: "PUT", body: JSON.stringify({ card_ids: cardIds }) },
    apiBaseUrl
  );
  console.log(PQ_LOG, "2. Received HTTP response", {
    status: response.status,
    ok: response.ok,
  });

  const data = await parseJsonResponse(response);
  console.log(PQ_LOG, "3. Parsed response body", data);

  if (!response.ok) {
    console.error(PQ_LOG, "3a. Request failed", { status: response.status, data });
    throw new Error(
      data?.error ||
        data?.detail ||
        `Failed to update price/quantity on eBay: ${response.status}`
    );
  }

  console.log(PQ_LOG, "3b. Queued successfully", {
    update_run_id: data?.update_run_id,
    task_id: data?.task_id,
    status: data?.status,
    total: data?.total,
  });

  return data;
}

// Fields that can still be edited live on an already-listed card, since they
// map directly to eBay's bulkUpdatePriceQuantity payload (SKU + price + quantity).
export const EBAY_LIVE_EDITABLE_FIELDS = ["available_quantity", "sale_price"];

export function canEditListedCardField(card, fieldName) {
  return (
    card?.status === "listed" && EBAY_LIVE_EDITABLE_FIELDS.includes(fieldName)
  );
}
